const PROBE_TIMEOUT = 10_000;

/** Probe the existing terminal transport without resetting xterm or the PTY.
 * Servers advertise support with [control=4, heartbeat-v1=2]. Older servers
 * remain usable without probes. Heartbeats never enter terminal output/ACKs.
 */
export function terminalLiveness(socket: WebSocket, onStale: () => void) {
  let supported = false;
  let disposed = false;
  let sequence = 0;
  let pending: number | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;

  function suspend() {
    clearTimeout(timer);
    pending = null;
  }

  function armTimeout() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (disposed || pending === null) return;
      suspend();
      onStale();
    }, PROBE_TIMEOUT);
  }

  return {
    probe() {
      if (disposed || !supported || pending !== null || socket.readyState !== WebSocket.OPEN)
        return;
      pending = sequence = (sequence + 1) >>> 0;
      const frame = new Uint8Array(5);
      frame[0] = 0x06;
      new DataView(frame.buffer).setUint32(1, pending, false);
      armTimeout();
      try {
        socket.send(frame);
      } catch {
        suspend();
        onStale();
      }
    },
    receive(data: ArrayBuffer): boolean {
      if (disposed) return false;
      const frame = new Uint8Array(data);
      if (frame[0] === 0x04 && frame.length === 2 && frame[1] === 2) {
        supported = true;
        return true;
      }
      if (frame[0] === 0x07) {
        if (frame.length === 5 && new DataView(data).getUint32(1, false) === pending) suspend();
        return true;
      }
      // A congested downlink can delay the pong behind a large output packet.
      // Give it time while terminal data continues to arrive on that socket.
      if (pending !== null && (frame[0] === 0x02 || frame[0] === 0x05)) armTimeout();
      return false;
    },
    suspend,
    dispose() {
      disposed = true;
      suspend();
    },
  };
}
